import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { RequestsBoard } from './RequestsBoard/RequestsBoard';
import { RequestsPageInfo } from './RequestsPageInfo/RequestsPageInfo';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XlsBtn } from '~/ui';
import { ReportDrawer } from '~/components/ReportDrawer/ReportDrawer';
import { RequestsReportExtra } from './RequestsReportExtra/RequestsReportExtra';
import { downloadReportWithFields } from '~/utils/downloadReportFile';
import { Dayjs } from 'dayjs';

interface IAccommodation {
  id: number;
  name: string;
}

const Requests = (): JSX.Element => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reportType, setReportType] = useState(
    'ticket_accommodation_analytics_export'
  );
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [accommodations, setAccommodations] = useState<IAccommodation[]>([]);
  const [isAllAccommodationChecked, setIsAllAccommodationChecked] =
    useState(false);

  const getReportPathAndName = () => {
    const isBrief = reportType === 'ticket_accommodation_analytics_export';
    return {
      path: isBrief ? 'analytics-ticket-accommodation' : 'analytics-ticket-accommodation-date',
      name: isBrief ? 'analytics-request-accommodation' : 'analytics-request-accommodation-details',
    };
  };

  const currentFilters = {
    start_date: startDate?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    end_date: endDate?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    ...(isAllAccommodationChecked
      ? { accommodation_all: 1 }
      : { accommodation_ids: accommodations.map((a) => a.id) }),
  };

  const handleExport = async (fields: string[]) => {
    const { path, name } = getReportPathAndName();
    await downloadReportWithFields(path, name, { ...currentFilters, fields });
  };

  const handlePresetExport = async (presetId: number) => {
    const { path, name } = getReportPathAndName();
    await downloadReportWithFields(path, name, { preset_id: presetId });
  };

  return (
    <Page
      heading={
        <Heading
          text={t('request_list')}
          actions={
            <XlsBtn
              onClick={() => setDrawerOpen(true)}
              xlsBtnText={t('request_analytics')}
            />
          }
        />
      }
    >
      <RequestsPageInfo statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
      <RequestsBoard statusFilter={statusFilter} />
      <ReportDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        reportType={reportType}
        onExport={handleExport}
        onPresetExport={handlePresetExport}
        extraParams={currentFilters}
        isExportDisabled={
          (!accommodations.length && !isAllAccommodationChecked) ||
          !startDate ||
          !endDate
        }
        title={t('request_analytics')}
        extraContent={
          <RequestsReportExtra
            reportType={reportType}
            onReportTypeChange={setReportType}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            accommodations={accommodations}
            onAccommodationsChange={setAccommodations}
            isAllAccommodationChecked={isAllAccommodationChecked}
            onAllAccommodationChange={setIsAllAccommodationChecked}
          />
        }
      />
    </Page>
  );
};

export default Requests;
